import {
  addCustomerAddress,
  deleteCustomerAddress,
  getClient,
  getCustomer,
  getCustomerAddresses,
  getCustomization,
  updateCustomerAddress
} from "@/modules/firestore_repo";
import { loadCustomer } from "@/modules/functions_repo";
import * as segmentation from "@/modules/customer_order_segmentation_context";

const state = {
  canAddShipTo: false,
  canSeePrice: false,
  clientId: "",
  country: "",
  customerId: "",
  deliveryOptions: null,
  distributionChannel: "",
  division: "",
  divisionLabel: "",
  endCustomers: [],
  isIndustryRequired: false,
  isWithEndCustomer: false,
  name1: "",
  number: "",
  orderTypes: null,
  productHierarchies: [],
  salesOrganisation: "",
  shipTos: [],
  permittedCountries: null,
  additionalDivisionForSearch: "",

  customizationId: "",
  isOptionalOemEnabled: false,

  ...segmentation.state
};

const getters = {
  canAddShipTo: state => {
    return state.canAddShipTo || false;
  },
  canSeePrice: state => {
    return state.canSeePrice || false;
  },
  getClientId: state => {
    return state.clientId || "";
  },
  getCountry: state => {
    return state.country || "";
  },
  getCustomerId: state => {
    return state.customerId || "";
  },
  getDeliveryOptions: state => {
    return state.deliveryOptions || [];
  },
  getDivision: state => {
    return state.division || "";
  },
  getDivisionLabel: state => {
    return state.divisionLabel || "";
  },
  getEndCustomers: state => {
    return state.endCustomers || [];
  },
  getName: state => {
    return state.name1 || "";
  },
  getNumber: state => {
    return state.number || "";
  },
  getOrderTypes: state => {
    return state.orderTypes || [];
  },
  getProductHierarchies: state => {
    return state.productHierarchies || [];
  },
  getShipTos: state => {
    return state.shipTos || [];
  },
  getPermittedCountries: state => {
    return state.permittedCountries || [];
  },
  isIndustryRequired: state => {
    return state.isIndustryRequired || false;
  },
  isWithEndCustomer: state => {
    return state.isWithEndCustomer || false;
  },
  getAdditionalDivisionForSearch: state => {
    return state.additionalDivisionForSearch || "";
  },

  ...segmentation.getters
};

const actions = {
  async addAddress({ state, dispatch }, { collection, data }) {
    const address = await addCustomerAddress(
      state.customerId,
      collection,
      data
    );
    await dispatch("fetchAddresses", state.customerId);
    return address;
  },
  async deleteAddress({ state, dispatch }, { id, collection }) {
    await deleteCustomerAddress(state.customerId, collection, id);
    await dispatch("fetchAddresses", state.customerId);
  },
  async fetchAddresses({ state, commit }) {
    getCustomerAddresses(state.customerId, "shipTos").then(value => {
      commit("setShipTos", value);
    });
    getCustomerAddresses(state.customerId, "endCustomers").then(value => {
      commit("setEndCustomers", value);
    });
  },
  async fetchClientInfo({ state, commit }) {
    const clientInfo = await getClient(state.clientId);
    commit("setClientInfo", clientInfo);
  },
  async fetchCustomization({ state, commit }) {
    const data = await getCustomization(state.customerId);
    commit("setDeliveryOptions", data.deliveryOptions);
    commit("setOrderTypes", data.orderTypes);
    commit("setCanSeePrice", data.canSeePrice);
    commit("setIsIndustryRequired", data.isIndustryRequired);
    commit("setIsWithEndCustomer", data.isWithEndCustomer);
  },
  async fetchPermittedCountries({ commit }, { country, permittedCountries }) {
    const data = permittedCountries || [country];
    commit("setPermittedCountries", data);
  },
  async switchCustomer({ commit, dispatch }, { customerId }) {
    commit("setCustomerId", customerId);
    commit("basket/reset", null, { root: true });
    commit("tableToBasketReset", null, { root: true });
    commit("orderStatusReset", null, { root: true });
    loadCustomer(customerId);
    const customer = await getCustomer(customerId);
    commit("setCustomerData", customer);
    commit("basket/setBasketId", customer.basketId, { root: true });
    commit("setCustomizationId", customer.customization);
    commit("setIsOptionalOemEnabled", customer.isOptionalOemEnabled || false);

    const p = [
      dispatch("fetchClientInfo"),
      dispatch("fetchCustomization"),
      dispatch("fetchAddresses"),
      dispatch("fetchPermittedCountries", customer)
    ];
    if (customer.basketId) {
      p.push(dispatch("basket/fetchBasket", null, { root: true }));
    }
    await Promise.all(p);
  },
  async updateAddress({ state, dispatch }, { id, collection, data }) {
    await updateCustomerAddress(state.customerId, collection, id, data);
    await dispatch("fetchAddresses", state.customerId);
  },

  ...segmentation.actions
};

const mutations = {
  clearCustomerData: state => {
    state.customerId = "";
    state.number = "";
    state.name1 = "";
    state.country = "";
    state.clientId = "";
    state.salesOrganisation = "";
    state.distributionChannel = "";
    state.division = "";
    state.divisionLabel = "";
    state.deliveryOptions = null;
    state.orderTypes = null;
    state.productHierarchies = [];
    state.shipTos = [];
    state.endCustomers = [];
    state.additionalDivisionForSearch = "";
  },
  setCanSeePrice: (state, payload) => {
    state.canSeePrice = payload;
  },
  setClientInfo: (
    state,
    {
      distributionChannel,
      division,
      divisionLabel,
      salesOrganisation,
      additionalDivisionForSearch
    }
  ) => {
    state.salesOrganisation = salesOrganisation;
    state.distributionChannel = distributionChannel;
    state.division = division;
    state.divisionLabel = divisionLabel;
    state.additionalDivisionForSearch = additionalDivisionForSearch;
  },
  setCustomerData: (state, payload) => {
    state.number = payload.number;
    state.name1 = payload.name1;
    state.country = payload.country;
    state.clientId = payload.clientId;
    state.canAddShipTo = payload.canAddShipTo;
    state.productHierarchies = payload.productHierarchies;
  },
  setCustomerId: (state, customerId) => {
    state.customerId = customerId;
  },
  setDeliveryOptions: (state, payload) => {
    state.deliveryOptions = payload;
  },
  setEndCustomers: (state, payload) => {
    state.endCustomers = payload;
  },
  setIsIndustryRequired: (state, payload) => {
    state.isIndustryRequired = payload;
  },
  setIsWithEndCustomer: (state, payload) => {
    state.isWithEndCustomer = payload;
  },
  setOrderTypes: (state, payload) => {
    state.orderTypes = payload;
  },
  setShipTos: (state, payload) => {
    state.shipTos = payload;
  },
  setPermittedCountries: (state, payload) => {
    state.permittedCountries = payload;
  },
  setCustomizationId: (state, payload) => {
    state.customizationId = payload;
  },
  setIsOptionalOemEnabled: (state, payload) => {
    state.isOptionalOemEnabled = payload;
  },

  ...segmentation.mutations
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state
};
